/**
 * AI Tooltip Directive
 * 
 * Usage:
 * <button v-ai-tooltip="{ feature: 'add_hcp', trigger: 'hover' }">Add HCP</button>
 * <input v-ai-tooltip="{ feature: 'hcp_npi', trigger: 'struggle' }" />
 */

import Vue from 'vue';
import { getStore } from '@/store';
import SmartTooltip from '@/components/ai-assistant/SmartTooltip.vue';

class TooltipManager {
  constructor() {
    this.activeTooltips = new Map();
    this.elementTracking = new Map();
  }

  /**
   * Create and show tooltip for element
   */
  async showTooltip(el, binding, vnode) {
    const store = getStore();
    if (!store) {
      console.warn('[AI Tooltip] Store not available');
      return;
    }

    const config = this.parseConfig(binding.value);
    
    console.log('[AI Tooltip] Checking help for:', {
      feature: config.feature,
      elementId: config.elementId || this.getElementId(el),
      trigger: config.trigger,
      url: window.location.href,
    });
    
    // Check if help is available
    const result = await store.dispatch('proactiveHelp/checkHelp', {
      feature_area: config.feature,
      element_id: config.elementId || this.getElementId(el),
      trigger_type: config.trigger,
      page_url: window.location.href,
    });

    console.log('[AI Tooltip] Check help result:', result);

    if (!result.success || !result.help_items || result.help_items.length === 0) {
      console.warn('[AI Tooltip] No help items available for feature:', config.feature);
      return;
    }

    // Get first help item
    const helpItem = result.help_items[0];

    // Create tooltip instance
    const TooltipConstructor = Vue.extend(SmartTooltip);
    const tooltipInstance = new TooltipConstructor({
      propsData: {
        tooltip: helpItem,
        targetElement: el,
        position: config.position || 'auto',
        autoHide: config.autoHide !== false,
        autoHideDelay: config.autoHideDelay || 10000,
      },
      parent: vnode.context,
    });

    // Mount tooltip
    tooltipInstance.$mount();
    document.body.appendChild(tooltipInstance.$el);

    // Store instance
    this.activeTooltips.set(el, tooltipInstance);

    console.log('[AI Tooltip] Tooltip created and mounted for:', config.feature);

    // Listen to events
    tooltipInstance.$on('hide', () => {
      this.destroyTooltip(el);
    });

    tooltipInstance.$on('dismiss', (data) => {
      this.destroyTooltip(el);
    });

    // Dispatch show action
    store.dispatch('proactiveHelp/showTooltip', {
      tooltip: helpItem,
      element: el,
    });
  }

  /**
   * Destroy tooltip instance
   */
  destroyTooltip(el) {
    const tooltipInstance = this.activeTooltips.get(el);
    
    if (tooltipInstance) {
      tooltipInstance.$destroy();
      if (tooltipInstance.$el && tooltipInstance.$el.parentNode) {
        tooltipInstance.$el.parentNode.removeChild(tooltipInstance.$el);
      }
      this.activeTooltips.delete(el);
    }
  }

  /**
   * Parse directive config
   */
  parseConfig(value) {
    if (typeof value === 'string') {
      return { feature: value, trigger: 'hover' };
    }

    return {
      feature: value.feature || 'general',
      elementId: value.elementId || null,
      trigger: value.trigger || 'hover',
      position: value.position || 'auto',
      delay: value.delay || 3000,
      autoHide: value.autoHide !== false,
      autoHideDelay: value.autoHideDelay || 10000,
      helpText: value.helpText || null,
    };
  }

  /**
   * Get element identifier
   */
  getElementId(el) {
    return el.id || 
           el.name || 
           el.getAttribute('data-tooltip-id') ||
           `${el.tagName.toLowerCase()}.${el.className.split(' ')[0] || 'unnamed'}`;
  }

  /**
   * Setup hover trigger
   */
  setupHoverTrigger(el, binding, vnode) {
    const config = this.parseConfig(binding.value);
    let hoverTimer = null;

    const handleMouseEnter = () => {
      hoverTimer = setTimeout(() => {
        this.showTooltip(el, binding, vnode);
      }, config.delay);
    };

    const handleMouseLeave = () => {
      if (hoverTimer) {
        clearTimeout(hoverTimer);
        hoverTimer = null;
      }
    };

    el.addEventListener('mouseenter', handleMouseEnter);
    el.addEventListener('mouseleave', handleMouseLeave);

    // Store cleanup functions
    this.elementTracking.set(el, {
      cleanup: () => {
        el.removeEventListener('mouseenter', handleMouseEnter);
        el.removeEventListener('mouseleave', handleMouseLeave);
        if (hoverTimer) clearTimeout(hoverTimer);
      },
    });
  }

  /**
   * Setup struggle trigger (form fields)
   */
  setupStruggleTrigger(el, binding, vnode) {
    if (!['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)) return;

    let focusStartTime = null;
    let initialValue = '';
    let struggleTimer = null;

    const handleFocus = () => {
      focusStartTime = Date.now();
      initialValue = el.value || '';
      
      // Check for struggle after 8 seconds
      struggleTimer = setTimeout(() => {
        const currentValue = el.value || '';
        const inputDelta = currentValue.length - initialValue.length;
        
        // If minimal input after 8 seconds, show help
        if (inputDelta < 3) {
          this.showTooltip(el, binding, vnode);
        }
      }, 8000);
    };

    const handleBlur = () => {
      if (struggleTimer) {
        clearTimeout(struggleTimer);
        struggleTimer = null;
      }
      focusStartTime = null;
    };

    const handleInput = () => {
      // Reset timer on input
      if (struggleTimer) {
        clearTimeout(struggleTimer);
      }
    };

    el.addEventListener('focus', handleFocus);
    el.addEventListener('blur', handleBlur);
    el.addEventListener('input', handleInput);

    this.elementTracking.set(el, {
      cleanup: () => {
        el.removeEventListener('focus', handleFocus);
        el.removeEventListener('blur', handleBlur);
        el.removeEventListener('input', handleInput);
        if (struggleTimer) clearTimeout(struggleTimer);
      },
    });
  }

  /**
   * Setup manual trigger (click)
   */
  setupManualTrigger(el, binding, vnode) {
    const handleClick = (e) => {
      e.preventDefault();
      this.showTooltip(el, binding, vnode);
    };

    el.addEventListener('click', handleClick);

    this.elementTracking.set(el, {
      cleanup: () => {
        el.removeEventListener('click', handleClick);
      },
    });
  }

  /**
   * Setup first-time trigger
   */
  setupFirstTimeTrigger(el, binding, vnode) {
    const config = this.parseConfig(binding.value);
    const storageKey = `ai_tooltip_shown_${config.feature}_${this.getElementId(el)}`;
    
    // Check if already shown
    if (localStorage.getItem(storageKey)) return;

    // Show after delay
    setTimeout(() => {
      this.showTooltip(el, binding, vnode);
      localStorage.setItem(storageKey, 'true');
    }, config.delay);
  }

  /**
   * Cleanup element tracking
   */
  cleanup(el) {
    const tracking = this.elementTracking.get(el);
    if (tracking && tracking.cleanup) {
      tracking.cleanup();
    }
    this.elementTracking.delete(el);
    this.destroyTooltip(el);
  }
}

// Create singleton instance
const tooltipManager = new TooltipManager();

// Vue directive definition
const aiTooltip = {
  bind(el, binding, vnode) {
    const store = getStore();
    
    // Check if proactive help is enabled
    if (!store || !store.getters['proactiveHelp/isEnabled']) {
      console.warn('[AI Tooltip] Proactive help is disabled or store not available');
      return;
    }
    
    const config = tooltipManager.parseConfig(binding.value);
    console.log('[AI Tooltip] Directive bound to element:', {
      feature: config.feature,
      trigger: config.trigger,
      element: el.tagName,
    });

    // Setup trigger based on type
    switch (config.trigger) {
      case 'hover':
        tooltipManager.setupHoverTrigger(el, binding, vnode);
        break;
      
      case 'struggle':
        tooltipManager.setupStruggleTrigger(el, binding, vnode);
        break;
      
      case 'manual':
        tooltipManager.setupManualTrigger(el, binding, vnode);
        break;
      
      case 'first_time':
        tooltipManager.setupFirstTimeTrigger(el, binding, vnode);
        break;
      
      default:
        tooltipManager.setupHoverTrigger(el, binding, vnode);
    }

    // Listen for struggle detection events
    const handleStruggleDetected = (event) => {
      if (event.detail.is_struggling) {
        tooltipManager.showTooltip(el, binding, vnode);
      }
    };

    window.addEventListener('ai-struggle-detected', handleStruggleDetected);

    // Store event listener for cleanup
    if (!tooltipManager.elementTracking.has(el)) {
      tooltipManager.elementTracking.set(el, { cleanup: () => {} });
    }
    
    const tracking = tooltipManager.elementTracking.get(el);
    const originalCleanup = tracking.cleanup;
    tracking.cleanup = () => {
      originalCleanup();
      window.removeEventListener('ai-struggle-detected', handleStruggleDetected);
    };
  },

  update(el, binding, vnode) {
    // If config changed significantly, rebind
    const oldConfig = tooltipManager.parseConfig(binding.oldValue);
    const newConfig = tooltipManager.parseConfig(binding.value);

    if (oldConfig.trigger !== newConfig.trigger || 
        oldConfig.feature !== newConfig.feature) {
      tooltipManager.cleanup(el);
      aiTooltip.bind(el, binding, vnode);
    }
  },

  unbind(el) {
    tooltipManager.cleanup(el);
  },
};

// Register directive globally
Vue.directive('ai-tooltip', aiTooltip);

export default aiTooltip;
